import { useEffect, useRef, useState } from "react";
import { setUnitSystem, setDisplayCurrency, convertStoreCurrency, isZipBytes, readBackupZip, type CanonicalStore } from "../../core/index.ts";
import { longDate } from "../format.ts";
import { THEMES, saveTheme, applyTheme, type Palette } from "../themes.ts";

const CURRENCIES_TOP = [
  { code: "USD", label: "US Dollar" },
  { code: "EUR", label: "Euro" },
  { code: "GBP", label: "Pound Sterling" },
  { code: "RON", label: "Romanian Leu" },
  { code: "JPY", label: "Japanese Yen" },
  { code: "CAD", label: "Canadian Dollar" },
  { code: "AUD", label: "Australian Dollar" },
  { code: "CHF", label: "Swiss Franc" },
  { code: "CNY", label: "Chinese Yuan" },
  { code: "NOK", label: "Norwegian Krone" },
  { code: "SEK", label: "Swedish Krona" },
  { code: "DKK", label: "Danish Krone" },
  { code: "PLN", label: "Polish Zloty" },
  { code: "CZK", label: "Czech Koruna" },
  { code: "HUF", label: "Hungarian Forint" },
];

const CURRENCIES_REST = [
  { code: "AFN", label: "Afghan Afghani" },
  { code: "ALL", label: "Albanian Lek" },
  { code: "DZD", label: "Algerian Dinar" },
  { code: "AOA", label: "Angolan Kwanza" },
  { code: "AMD", label: "Armenian Dram" },
  { code: "AWG", label: "Aruban Florin" },
  { code: "AZN", label: "Azerbaijani Manat" },
  { code: "BSD", label: "Bahamian Dollar" },
  { code: "BHD", label: "Bahraini Dinar" },
  { code: "BDT", label: "Bangladeshi Taka" },
  { code: "BBD", label: "Barbadian Dollar" },
  { code: "BYN", label: "Belarusian Ruble" },
  { code: "BZD", label: "Belize Dollar" },
  { code: "BMD", label: "Bermudian Dollar" },
  { code: "BTN", label: "Bhutanese Ngultrum" },
  { code: "BOB", label: "Bolivian Boliviano" },
  { code: "BAM", label: "Bosnian Convertible Mark" },
  { code: "BWP", label: "Botswana Pula" },
  { code: "BRL", label: "Brazilian Real" },
  { code: "BND", label: "Brunei Dollar" },
  { code: "BGN", label: "Bulgarian Lev" },
  { code: "BIF", label: "Burundian Franc" },
  { code: "KHR", label: "Cambodian Riel" },
  { code: "CVE", label: "Cape Verdean Escudo" },
  { code: "KYD", label: "Cayman Islands Dollar" },
  { code: "XAF", label: "Central African CFA Franc" },
  { code: "XPF", label: "CFP Franc" },
  { code: "CLP", label: "Chilean Peso" },
  { code: "COP", label: "Colombian Peso" },
  { code: "KMF", label: "Comorian Franc" },
  { code: "CDF", label: "Congolese Franc" },
  { code: "CRC", label: "Costa Rican Colon" },
  { code: "HRK", label: "Croatian Kuna" },
  { code: "CUC", label: "Cuban Convertible Peso" },
  { code: "CUP", label: "Cuban Peso" },
  { code: "DJF", label: "Djiboutian Franc" },
  { code: "DOP", label: "Dominican Peso" },
  { code: "XCD", label: "East Caribbean Dollar" },
  { code: "EGP", label: "Egyptian Pound" },
  { code: "ERN", label: "Eritrean Nakfa" },
  { code: "ETB", label: "Ethiopian Birr" },
  { code: "FKP", label: "Falkland Islands Pound" },
  { code: "FJD", label: "Fiji Dollar" },
  { code: "GMD", label: "Gambian Dalasi" },
  { code: "GEL", label: "Georgian Lari" },
  { code: "GHS", label: "Ghanaian Cedi" },
  { code: "GIP", label: "Gibraltar Pound" },
  { code: "GTQ", label: "Guatemalan Quetzal" },
  { code: "GGP", label: "Guernsey Pound" },
  { code: "GNF", label: "Guinean Franc" },
  { code: "GYD", label: "Guyanese Dollar" },
  { code: "HTG", label: "Haitian Gourde" },
  { code: "HNL", label: "Honduran Lempira" },
  { code: "HKD", label: "Hong Kong Dollar" },
  { code: "ISK", label: "Icelandic Krona" },
  { code: "INR", label: "Indian Rupee" },
  { code: "IDR", label: "Indonesian Rupiah" },
  { code: "IRR", label: "Iranian Rial" },
  { code: "IQD", label: "Iraqi Dinar" },
  { code: "IMP", label: "Isle of Man Pound" },
  { code: "ILS", label: "Israeli Shekel" },
  { code: "JMD", label: "Jamaican Dollar" },
  { code: "JEP", label: "Jersey Pound" },
  { code: "JOD", label: "Jordanian Dinar" },
  { code: "KZT", label: "Kazakhstani Tenge" },
  { code: "KES", label: "Kenyan Shilling" },
  { code: "KWD", label: "Kuwaiti Dinar" },
  { code: "KGS", label: "Kyrgyzstani Som" },
  { code: "LAK", label: "Lao Kip" },
  { code: "LBP", label: "Lebanese Pound" },
  { code: "LSL", label: "Lesotho Loti" },
  { code: "LRD", label: "Liberian Dollar" },
  { code: "LYD", label: "Libyan Dinar" },
  { code: "MOP", label: "Macanese Pataca" },
  { code: "MKD", label: "Macedonian Denar" },
  { code: "MGA", label: "Malagasy Ariary" },
  { code: "MWK", label: "Malawian Kwacha" },
  { code: "MYR", label: "Malaysian Ringgit" },
  { code: "MVR", label: "Maldivian Rufiyaa" },
  { code: "MRU", label: "Mauritanian Ouguiya" },
  { code: "MUR", label: "Mauritian Rupee" },
  { code: "MXN", label: "Mexican Peso" },
  { code: "MDL", label: "Moldovan Leu" },
  { code: "MNT", label: "Mongolian Tugrik" },
  { code: "MAD", label: "Moroccan Dirham" },
  { code: "MZN", label: "Mozambican Metical" },
  { code: "MMK", label: "Myanmar Kyat" },
  { code: "NAD", label: "Namibian Dollar" },
  { code: "NPR", label: "Nepalese Rupee" },
  { code: "ANG", label: "Netherlands Antillean Guilder" },
  { code: "NZD", label: "New Zealand Dollar" },
  { code: "NIO", label: "Nicaraguan Cordoba" },
  { code: "NGN", label: "Nigerian Naira" },
  { code: "OMR", label: "Omani Rial" },
  { code: "PKR", label: "Pakistani Rupee" },
  { code: "PAB", label: "Panamanian Balboa" },
  { code: "PGK", label: "Papua New Guinean Kina" },
  { code: "PYG", label: "Paraguayan Guarani" },
  { code: "PEN", label: "Peruvian Sol" },
  { code: "PHP", label: "Philippine Peso" },
  { code: "QAR", label: "Qatari Riyal" },
  { code: "RUB", label: "Russian Ruble" },
  { code: "RWF", label: "Rwandan Franc" },
  { code: "SHP", label: "Saint Helena Pound" },
  { code: "SVC", label: "Salvadoran Colon" },
  { code: "WST", label: "Samoan Tala" },
  { code: "STN", label: "Sao Tomean Dobra" },
  { code: "SAR", label: "Saudi Riyal" },
  { code: "RSD", label: "Serbian Dinar" },
  { code: "SCR", label: "Seychellois Rupee" },
  { code: "SLE", label: "Sierra Leonean Leone" },
  { code: "SGD", label: "Singapore Dollar" },
  { code: "SBD", label: "Solomon Islands Dollar" },
  { code: "SOS", label: "Somali Shilling" },
  { code: "ZAR", label: "South African Rand" },
  { code: "KRW", label: "South Korean Won" },
  { code: "SSP", label: "South Sudanese Pound" },
  { code: "LKR", label: "Sri Lankan Rupee" },
  { code: "SDG", label: "Sudanese Pound" },
  { code: "SRD", label: "Surinamese Dollar" },
  { code: "SZL", label: "Swazi Lilangeni" },
  { code: "TWD", label: "Taiwan Dollar" },
  { code: "TJS", label: "Tajikistani Somoni" },
  { code: "TZS", label: "Tanzanian Shilling" },
  { code: "THB", label: "Thai Baht" },
  { code: "TOP", label: "Tongan Paanga" },
  { code: "TTD", label: "Trinidad & Tobago Dollar" },
  { code: "TND", label: "Tunisian Dinar" },
  { code: "TRY", label: "Turkish Lira" },
  { code: "TMT", label: "Turkmenistani Manat" },
  { code: "AED", label: "UAE Dirham" },
  { code: "UGX", label: "Ugandan Shilling" },
  { code: "UAH", label: "Ukrainian Hryvnia" },
  { code: "UYU", label: "Uruguayan Peso" },
  { code: "UZS", label: "Uzbekistani Som" },
  { code: "VUV", label: "Vanuatu Vatu" },
  { code: "VES", label: "Venezuelan Bolivar" },
  { code: "VND", label: "Vietnamese Dong" },
  { code: "XOF", label: "West African CFA Franc" },
  { code: "YER", label: "Yemeni Rial" },
  { code: "ZMW", label: "Zambian Kwacha" },
  { code: "ZWL", label: "Zimbabwean Dollar" },
];

interface Props {
  store: CanonicalStore;
  save: (s: CanonicalStore) => void;
  isPro: boolean;
  lastExportAt: number | null;
  onImport: (text: string, attachments?: Map<string, Uint8Array>) => void;
  onExport: () => void;
  onClear: () => void;
  onUnlock: () => void;
  requirePro: () => void;
  theme: Palette;
  setTheme: (p: Palette) => void;
  onOpenCustomTheme: () => void;
}

/** Full-width select styled like the .linkrow action buttons (Export/Import/Delete). */
const selectBtn: React.CSSProperties = {
  width: "100%", border: "1px solid var(--edge)", borderRadius: "var(--radius)", padding: "12px",
  fontFamily: "var(--mono)", fontSize: 11, letterSpacing: 1.4, color: "var(--amber)",
  background: "var(--panel2)", cursor: "pointer",
};

export function SettingsView({ store, save, isPro, lastExportAt, onImport, onExport, onClear, onUnlock, theme, setTheme, onOpenCustomTheme }: Props) {
  const metric = store.profile.unitSystem === "metric";
  const importRef = useRef<HTMLInputElement>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);

  // Fullscreen (hides the browser's top/bottom bars) — supported on Android,
  // tablets and desktop; iOS Safari doesn't allow it, so we hide the button there.
  const fsSupported = typeof document !== "undefined" && typeof document.documentElement.requestFullscreen === "function";
  const [isFs, setIsFs] = useState(typeof document !== "undefined" && !!document.fullscreenElement);
  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggleFs = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const handleCurrencyChange = async (next: string) => {
    if (next === store.profile.displayCurrency) return;
    setConverting(true);
    setErrorMsg(null);
    const from = store.profile.displayCurrency;
    try {
      const res = await fetch(`https://open.er-api.com/v6/latest/${encodeURIComponent(from)}`);
      if (!res.ok) throw new Error("HTTP " + res.status);
      const json = (await res.json()) as { result?: string; rates?: Record<string, number> };
      if (json.result !== "success") throw new Error("API error");
      const rate = json.rates?.[next];
      if (rate == null) throw new Error("No rate for pair");
      const converted = convertStoreCurrency(store, from, next, rate);
      save(converted);
    } catch {
      const relabelled = setDisplayCurrency(store, next);
      save(relabelled);
      setErrorMsg(`No exchange rate available for ${from} → ${next} — label changed but amounts are still in ${from}.`);
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="container glow">
      <div className="eyebrow amber" style={{ paddingTop: 8 }}>APP · SETTINGS</div>
      <h1 className="headline">PIT <span className="accent">wall.</span></h1>

      <div className="divider"><span className="label">ABOUT</span><span className="line" /></div>
      <div className="mono muted" style={{ fontSize: 11, lineHeight: 1.7 }}>
        CarSpend Web · local-first. Your data is stored only in this browser — no account, no cloud.
        Move it between the web and your phone with Export / Import.
      </div>

      <div className="divider"><span className="label">CARSPEND PRO</span><span className="line" /></div>
      {isPro ? (
        <div className="panel"><span className="mono lime" style={{ fontSize: 12, letterSpacing: 1.4 }}>PRO · ACTIVE</span></div>
      ) : (
        <button className="panel" style={{ width: "100%", textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={onUnlock}>
          <span className="mono amber" style={{ fontSize: 12, letterSpacing: 1.4 }}>★ UNLOCK PRO</span>
          <span className="mono muted" style={{ fontSize: 14 }}>›</span>
        </button>
      )}

      <div className="divider"><span className="label">UNITS</span><span className="line" /></div>
      <select
        value={metric ? "metric" : "imperial"}
        onChange={(e) => save(setUnitSystem(store, e.target.value as "metric" | "imperial"))}
        style={selectBtn}
      >
        <option value="metric">METRIC · km, L, L/100km</option>
        <option value="imperial">IMPERIAL · mi, gal, MPG</option>
      </select>

      <div className="divider"><span className="label">CURRENCY</span><span className="line" /></div>
      <select
        value={store.profile.displayCurrency}
        onChange={(e) => handleCurrencyChange(e.target.value)}
        disabled={converting}
        style={{ ...selectBtn, cursor: converting ? "wait" : "pointer" }}
      >
        <optgroup label="━ TOP">
          {CURRENCIES_TOP.map((c) => (
            <option key={c.code} value={c.code}>{c.code} · {c.label}</option>
          ))}
        </optgroup>
        <optgroup label="━ ALL CURRENCIES">
          {CURRENCIES_REST.map((c) => (
            <option key={c.code} value={c.code}>{c.code} · {c.label}</option>
          ))}
        </optgroup>
      </select>
      {converting && <div className="mono muted" style={{ fontSize: 10, marginTop: 6 }}>converting…</div>}
      {errorMsg && (
        <div className="panel" style={{ marginTop: 8, borderColor: "var(--redline)", background: "color-mix(in srgb, var(--redline) 10%, transparent)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ color: "var(--redline)", fontSize: 16, lineHeight: 1.4 }}>⚠</span>
            <span className="mono" style={{ fontSize: 11, color: "var(--creamDim)", lineHeight: 1.5 }}>{errorMsg}</span>
            <button
              onClick={() => setErrorMsg(null)}
              style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1.4 }}
            >✕</button>
          </div>
        </div>
      )}
      <div className="mono muted" style={{ fontSize: 9, marginTop: 6 }}>Changing currency converts all stored amounts via live rates (166 currencies). Requires internet — falls back to relabel if offline.</div>

      <div className="divider"><span className="label">APPEARANCE</span><span className="line" /></div>
      <select
        value={theme.name}
        onChange={(e) => {
          if (e.target.value === "custom") { onOpenCustomTheme(); return; }
          const p = THEMES.find((t) => t.name === e.target.value);
          if (p) { setTheme(p); saveTheme(p); applyTheme(p); }
        }}
        style={selectBtn}
      >
        {THEMES.map((t) => (
          <option key={t.name} value={t.name}>{t.label}</option>
        ))}
        <option value="custom">Custom…</option>
      </select>
      {fsSupported && (
        <button className="linkrow" style={{ justifyContent: "space-between", marginTop: 8 }} onClick={toggleFs}>
          <span className="amber">{isFs ? "EXIT FULL SCREEN" : "FULL SCREEN"}</span><span className="muted">⛶</span>
        </button>
      )}
      <div className="mono muted" style={{ fontSize: 9, marginTop: 6 }}>
        On a phone, add CarSpend to your home screen to run it full-screen with no browser bars.
      </div>

      <div className="divider"><span className="label">BACKUP</span><span className="line" /></div>
      <input ref={importRef} type="file" accept="application/zip,.zip,application/json,.json" hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) {
            const bytes = new Uint8Array(await f.arrayBuffer());
            if (isZipBytes(bytes)) { const x = readBackupZip(bytes); onImport(x.json, x.attachments); }
            else onImport(new TextDecoder().decode(bytes));
          }
          e.target.value = "";
        }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button className="linkrow" style={{ justifyContent: "space-between" }} onClick={() => onExport()}>
          <span className="amber">EXPORT APP BACKUP</span><span className="muted">›</span>
        </button>
        <button className="linkrow" style={{ justifyContent: "space-between" }} onClick={() => importRef.current?.click()}>
          <span className="amber">IMPORT APP BACKUP</span><span className="muted">›</span>
        </button>
        <div className="mono" style={{ fontSize: 9, color: lastExportAt ? "var(--muted)" : "var(--redline)" }}>
          {lastExportAt ? `Last backup: ${longDate(lastExportAt)}` : "No backup yet — your data lives only in this browser."}
        </div>
      </div>

      <div className="divider" style={{ marginTop: 40 }}><span className="label">DATA</span><span className="line" /></div>
      <button className="linkrow" style={{ justifyContent: "space-between", borderColor: "#e53935", color: "#e53935" }}
        onClick={() => { if (confirm("Erase all local data? Your current data is downloaded first.")) onClear(); }}>
        DELETE ALL DATA<span className="mono" style={{ fontSize: 14, color: "#e53935" }}>›</span>
      </button>
      <div style={{ height: 24 }} />
    </div>
  );
}
